"use client";

import React from "react";
import { ShieldCheck, Download, Lock, Activity, FileText, CircleAlert, CircleCheck, SquareTerminal, Layers3 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const auditLogs = [{
  id: "log_001",
  timestamp: "2023-10-27T10:42:01.001Z",
  tenant: "tn_8f92a",
  actor: "system",
  event: "Tenant isolation policy verified. Strict boundaries enforced.",
  status: "success"
}, {
  id: "log_002",
  timestamp: "2023-10-27T10:45:12.332Z",
  tenant: "tn_8f92a",
  actor: "j.doe@acme.com",
  event: "Uploaded document 'Q3_Report.pdf'. Status set to DRAFT.",
  status: "info"
}, {
  id: "log_003",
  timestamp: "2023-10-27T10:50:05.110Z",
  tenant: "tn_8f92a",
  actor: "compliance_officer",
  event: "Document 'Q3_Report.pdf' transitioned from DRAFT to APPROVED.",
  status: "success"
}, {
  id: "log_004",
  timestamp: "2023-10-27T11:02:22.845Z",
  tenant: "tn_8f92a",
  actor: "api_key_v2_...",
  event: "Query executed. 3 citations generated from APPROVED content only.",
  status: "info"
}, {
  id: "log_005",
  timestamp: "2023-10-27T11:05:44.901Z",
  tenant: "tn_8f92a",
  actor: "guest_user",
  event: "Access denied. Attempted to query DRAFT content.",
  status: "warning"
}, {
  id: "log_006",
  timestamp: "2023-10-27T11:15:00.000Z",
  tenant: "tn_3b19c",
  actor: "system",
  event: "BYO-Key validation successful for external LLM provider.",
  status: "success"
}, {
  id: "log_007",
  timestamp: "2023-10-27T11:20:33.412Z",
  tenant: "tn_3b19c",
  actor: "api_key_v1_...",
  event: "Capacity threshold reached (85%). Alert dispatched to billing admin.",
  status: "warning"
}, {
  id: "log_008",
  timestamp: "2023-10-27T11:25:10.105Z",
  tenant: "tn_8f92a",
  actor: "system",
  event: "Insufficient evidence in APPROVED content. Refusal policy triggered.",
  status: "info"
}, {
  id: "log_009",
  timestamp: "2023-10-27T11:30:00.000Z",
  tenant: "tn_8f92a",
  actor: "admin_smith",
  event: "Exported immutable audit log for compliance review.",
  status: "success"
}];
const getStatusIcon = (status: string) => {
  switch (status) {
    case "success":
      return <CircleCheck className="size-4 text-primary" />;
    case "warning":
      return <CircleAlert className="size-4 text-destructive" />;
    case "info":
    default:
      return <Activity className="size-4 text-muted-foreground" />;
  }
};
export default function IsolationAndAuditProtocols() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: logRef,
    isVisible: isLogVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="isolation-audit-protocols" className="bg-background py-16 lg:py-20 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={headerRef} className={cn("max-w-3xl mx-auto text-center mb-12 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Layers3 className="size-4" />
            <span>Audit & isolation</span>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-6">
            Audit visibility and tenant-aware boundaries
          </h2>

          <p className="text-lg text-muted-foreground leading-relaxed">
            EloRAG is built for environments where teams need to trace how content moves,
            how answers are generated, and how tenant separation is maintained across the platform.
          </p>
        </div>

        <div ref={logRef} className={cn("max-w-5xl mx-auto transition-all duration-1000 delay-200 ease-out", isLogVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="rounded-xl border border-border bg-card shadow-xl shadow-shadow-color/5 overflow-hidden flex flex-col">
            <div className="bg-muted/50 border-b border-border px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center size-8 rounded-md bg-background border border-border shadow-sm">
                  <SquareTerminal className="size-4 text-foreground" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    Live System Audit Log
                    <span className="relative flex size-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                      <span className="relative inline-flex rounded-full size-2 bg-primary"></span>
                    </span>
                  </h3>
                  <p className="text-xs text-muted-foreground font-mono mt-0.5">
                    WSS://API.ELORAG.COM/V1/AUDIT/STREAM
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-background font-mono text-[10px] uppercase tracking-wider">
                  <Lock className="size-3 mr-1" />
                  Encrypted
                </Badge>
              </div>
            </div>

            <ScrollArea className="h-[400px] md:h-[500px] w-full bg-background">
              <div className="flex flex-col min-w-[800px]">
                <div className="grid grid-cols-[180px_120px_160px_1fr] gap-4 px-6 py-3 border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground uppercase tracking-wider sticky top-0 z-10 backdrop-blur-sm">
                  <div>Timestamp (UTC)</div>
                  <div>Tenant ID</div>
                  <div>Actor</div>
                  <div>Event Description</div>
                </div>

                <div className="flex flex-col py-2">
                  {auditLogs.map((log, i) => <div key={log.id} data-index={i} className="grid grid-cols-[180px_120px_160px_1fr] gap-4 px-6 py-2.5 hover:bg-muted/40 transition-colors group border-l-2 border-transparent hover:border-primary">
                      <div className="font-mono text-xs text-muted-foreground flex items-center">
                        {log.timestamp}
                      </div>

                      <div className="font-mono text-xs text-foreground flex items-center">
                        <Badge variant="secondary" className="font-mono text-[10px] bg-secondary/50 text-secondary-foreground border-border/50">
                          {log.tenant}
                        </Badge>
                      </div>

                      <div className="font-mono text-xs text-muted-foreground flex items-center truncate pr-4" title={log.actor}>
                        {log.actor}
                      </div>

                      <div className="text-sm text-foreground flex items-center gap-3">
                        {getStatusIcon(log.status)}
                        <span className="group-hover:text-foreground transition-colors">
                          {log.event}
                        </span>
                      </div>
                    </div>)}
                </div>
              </div>
            </ScrollArea>

            <div className="bg-muted/30 border-t border-border px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <FileText className="size-4" />
                Logs are retained according to configured governance and retention controls.
              </p>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <Button variant="outline" size="sm" className="w-full sm:w-auto">
                  Configure Webhooks
                </Button>
                <Button size="sm" className="w-full sm:w-auto">
                  <Download className="size-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}